import { lazy, Suspense } from 'react';

import { Elements } from '@stripe/react-stripe-js';
import { Route, Switch, useParams } from 'react-router-dom';

import { ExternalRedirect } from '@m/components/ExternalRedirect';
import { getCustomerAppUrl } from '@m/utils/getCustomerAppUrl';
import { stripePromise } from '@m/utils/stripeLoader';
import { Page } from 'apps/pay/components';
import { useVisitDetails } from 'apps/pay/hooks/payment';
import VisitLandingPage from 'apps/pay/screens/VisitLandingPage';

const VisitCompletePage = lazy(() => import('apps/pay/screens/VisitCompletePage'));
const VisitReceiptPage = lazy(() => import('apps/pay/screens/VisitReceiptPage'));

// Shared wrapper for visit-based pages with Stripe Elements
function StripeElementsVisitPage({
  PageComponent,
  pageProps = {},
}: {
  PageComponent: React.ComponentType<any>;
  pageProps?: Record<string, any>;
}) {
  const { visitCode } = useParams<{ visitCode: string }>();
  const { data: visit, isPending, error } = useVisitDetails(visitCode);

  if (isPending) {
    return 'Loading...';
  }

  if (visit === undefined || error) {
    return 'No data';
  }

  const stripeOpts = visit?.clientSecret ? { clientSecret: visit.clientSecret } : undefined;

  return (
    <Elements stripe={stripePromise} options={stripeOpts}>
      <PageComponent
        visit={visit}
        visitCode={visitCode}
        usePaymentElement={!!stripeOpts}
        {...pageProps}
      />
    </Elements>
  );
}

export default function Pay() {
  return (
    <Page paddingBottom="24px">
      <Switch>
        <Route exact path="/">
          <ExternalRedirect to={getCustomerAppUrl()} />
        </Route>
        <Route exact path="/:visitCode">
          <StripeElementsVisitPage PageComponent={VisitLandingPage} />
        </Route>
        {/* Exit route if payment completed */}
        <Route exact path="/:visitCode/complete">
          <Suspense fallback={null}>
            <StripeElementsVisitPage PageComponent={VisitCompletePage} />
          </Suspense>
        </Route>
        {/* Exit route if "not my vehicle" */}
        <Route exact path="/:visitCode/drive-out">
          <Suspense fallback={null}>
            <StripeElementsVisitPage
              PageComponent={VisitCompletePage}
              pageProps={{ hasReceipt: false }}
            />
          </Suspense>
        </Route>
        <Route exact path="/:visitCode/receipt">
          <Suspense fallback={null}>
            <VisitReceiptPage />
          </Suspense>
        </Route>
      </Switch>
    </Page>
  );
}
